import React from "react";
import { Card } from "@/components/ui/card";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
  PieChart, Pie, Legend, LineChart, Line,
} from "recharts";
import { formatCurrency } from "@/lib/format";

export const C = {
  navy: "#1C4358", red: "#9C1E22", sky: "#9BB9CE", khaki: "#C4C89B", green: "#2F7D5A",
  amber: "#B7791F", charcoal: "#252221", slate: "#4B5563", line: "#E6EDF2",
};
export const PALETTE = [C.navy, C.red, C.sky, C.khaki, C.green, C.amber, "#6B5B95", "#8FB339", "#D68C45", "#5D8AA8"];

export const fmtMoney = (v) => formatCurrency(Number(v) || 0);

export const Panel = ({ title, subtitle, right, children, testid, className = "" }) => (
  <Card data-testid={testid} className={`border-[#E6EDF2] ${className}`} style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
    {(title || right) && (
      <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b" style={{ borderColor: C.line }}>
        <div>
          {title && <h3 className="font-display font-semibold text-[15px]" style={{ color: C.navy }}>{title}</h3>}
          {subtitle && <p className="text-[12px] mt-0.5" style={{ color: C.slate }}>{subtitle}</p>}
        </div>
        {right}
      </div>
    )}
    <div className="p-5">{children}</div>
  </Card>
);

export const StatCard = ({ label, value, tone = C.navy, hint, money = false, testid }) => (
  <Card data-testid={testid} className="p-4 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
    <div className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: C.slate }}>{label}</div>
    <div className="mt-1 font-display font-bold text-2xl tabular-nums" style={{ color: tone }}>
      {value == null ? "—" : money ? fmtMoney(value) : value}
    </div>
    {hint && <div className="text-[11px] mt-0.5" style={{ color: C.slate }}>{hint}</div>}
  </Card>
);

export const StatusBadge = ({ label, tone = C.navy }) => (
  <span className="text-[11px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ backgroundColor: `${tone}1A`, color: tone }}>{label}</span>
);

export const Empty = ({ children = "No data for the selected filters." }) => (
  <div className="py-12 text-center text-[13px]" style={{ color: C.slate }}>{children}</div>
);

const hasData = (d) => Array.isArray(d) && d.some((x) => (x.value || 0) !== 0);

export const PieCard = ({ title, data, money = false, testid, height = 260 }) => (
  <Panel title={title} testid={testid}>
    {!hasData(data) ? <Empty /> : (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={height * 0.32} innerRadius={height * 0.17} paddingAngle={2}>
            {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
          </Pie>
          <Tooltip formatter={(v) => (money ? fmtMoney(v) : v)} />
          <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
        </PieChart>
      </ResponsiveContainer>
    )}
  </Panel>
);

export const BarCard = ({ title, data, money = false, color = C.navy, testid, height = 260 }) => (
  <Panel title={title} testid={testid}>
    {!hasData(data) ? <Empty /> : (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} layout="vertical" margin={{ left: 10, right: 16 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EEF3F6" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11, fill: C.slate }} tickFormatter={money ? (v) => `$${(v / 1000).toFixed(0)}k` : undefined} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: C.slate }} width={140} />
          <Tooltip formatter={(v) => (money ? fmtMoney(v) : v)} cursor={{ fill: "rgba(155,185,206,0.12)" }} />
          <Bar dataKey="value" radius={[0, 6, 6, 0]}>
            {data.map((_, i) => <Cell key={i} fill={money ? color : PALETTE[i % PALETTE.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    )}
  </Panel>
);

export const LineCard = ({ title, data, color = C.navy, testid, height = 240 }) => (
  <Panel title={title} testid={testid}>
    {!hasData(data) ? <Empty>Not enough data yet.</Empty> : (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ left: 4, right: 12, top: 6 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EEF3F6" />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: C.slate }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: C.slate }} width={30} />
          <Tooltip />
          <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2.5} dot={{ r: 2 }} />
        </LineChart>
      </ResponsiveContainer>
    )}
  </Panel>
);

export const SimpleTable = ({ columns, rows, testid, money = [] }) => (
  <div className="overflow-x-auto rounded-lg border" style={{ borderColor: C.line }} data-testid={testid}>
    <table className="w-full border-collapse">
      <thead><tr style={{ background: "#F4F8FB" }}>
        {columns.map((c) => <th key={c.key} className="text-left font-semibold px-3 py-2 text-[12px] uppercase tracking-wide" style={{ color: C.slate }}>{c.label}</th>)}
      </tr></thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={columns.length} className="px-3 py-8 text-center text-[13px]" style={{ color: C.slate }}>No data for the selected filters.</td></tr>
        ) : rows.map((r, i) => (
          <tr key={i} className="border-t" style={{ borderColor: C.line }} data-testid={`${testid}-row-${i}`}>
            {columns.map((c) => (
              <td key={c.key} className="px-3 py-2 text-[13px] tabular-nums" style={{ color: C.charcoal }}>
                {r[c.key] == null || r[c.key] === "" ? "—" : money.includes(c.key) ? fmtMoney(r[c.key]) : String(r[c.key])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ---- CSV ----
const csvCell = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
export const downloadCsv = (filename, rows) => {
  if (!rows || !rows.length) return;
  const headers = Array.from(rows.reduce((set, r) => { Object.keys(r).forEach((k) => set.add(k)); return set; }, new Set()));
  const lines = [headers.join(",")];
  rows.forEach((r) => lines.push(headers.map((h) => csvCell(r[h])).join(",")));
  const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

// chart arrays -> flat csv rows
export const chartsToCsv = (filename, chartMap) => {
  const rows = [];
  Object.entries(chartMap).forEach(([chart, arr]) => (arr || []).forEach((d) => rows.push({ chart, name: d.name, value: d.value })));
  downloadCsv(filename, rows);
};

// ---- Print report (standalone window) ----
export const openPrintReport = (title, scopeLabel, sections) => {
  const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
  const secHtml = sections.map((sec) => {
    const rows = (sec.rows || []).map((r) => `<tr>${sec.columns.map((c) => `<td>${esc(r[c.key])}</td>`).join("")}</tr>`).join("");
    return `<h2>${esc(sec.title)}</h2><table><tr>${sec.columns.map((c) => `<th>${esc(c.label)}</th>`).join("")}</tr>${rows || `<tr><td colspan="${sec.columns.length}">No data</td></tr>`}</table>`;
  }).join("");
  const doc = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title>
  <style>body{font-family:'Work Sans',Arial,sans-serif;color:#252221;margin:32px}h1{color:#1C4358;font-size:22px;margin:0 0 4px}
  h2{color:#1C4358;font-size:15px;margin:20px 0 6px;border-bottom:2px solid #E6EDF2;padding-bottom:4px}
  .sub{color:#4B5563;font-size:12px;margin-bottom:12px}table{width:100%;border-collapse:collapse;font-size:11.5px;margin-top:4px}
  th,td{border:1px solid #E6EDF2;padding:5px 8px;text-align:left}th{background:#F4F8FB;color:#1C4358}</style></head>
  <body><h1>MovingMyMama — ${esc(title)}</h1><div class="sub">${esc(scopeLabel)} · Generated ${new Date().toLocaleString()}</div>
  <div class="sub">Internal, aggregate-first report. No private client data. No fabricated metrics.</div>${secHtml}
  <script>window.onload=function(){setTimeout(function(){window.print()},300)}</script></body></html>`;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.open(); w.document.write(doc); w.document.close();
};
